import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

function Eyebrow({ children }) {
  return <p className="label-x mb-4">{children}</p>;
}

function FeaturedProducts({ data }) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    const params = new URLSearchParams({ limit: data.limit || 4, sort: "featured" });
    if (data.collection) params.set("collection", data.collection);
    api.get(`/products?${params}`).then(({ data: d }) => setProducts(d.products)).catch(() => {});
  }, [data.collection, data.limit]);
  return (
    <section className="onusa-container py-16 md:py-24" data-testid="section-featured-products">
      <div className="flex items-end justify-between mb-10">
        <div>
          <Eyebrow>{data.eyebrow}</Eyebrow>
          <h2 className="heading-x text-2xl md:text-4xl">{data.heading}</h2>
        </div>
        <Link to="/shop" className="label-x hidden md:block hover:opacity-60">BEKIJK ALLES →</Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
        {products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
}

function Hero({ data }) {
  return (
    <section className="relative h-[82vh] min-h-[560px] w-full overflow-hidden" data-testid="section-hero">
      {data.video_url ? (
        <video src={data.video_url} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <img src={mediaUrl(data.image)} alt={data.heading} className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#1D1D1B]/60 via-transparent to-[#1D1D1B]/10" />
      <div className="relative onusa-container h-full flex flex-col justify-end pb-16 md:pb-24">
        <p className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-[#F7F5F1]/80 reveal-up">{data.eyebrow}</p>
        <h1 className="heading-x text-[#F7F5F1] text-4xl sm:text-5xl md:text-6xl max-w-3xl mt-4 reveal-up" style={{ animationDelay: "0.1s" }}>{data.heading}</h1>
        {data.subheading && <p className="text-[#F7F5F1]/85 mt-4 max-w-md reveal-up" style={{ animationDelay: "0.2s" }}>{data.subheading}</p>}
        <div className="flex flex-wrap gap-4 mt-8 reveal-up" style={{ animationDelay: "0.3s" }}>
          {data.cta_label && <Link to={data.cta_href || "/shop"} className="btn-x" data-testid="hero-cta-primary">{data.cta_label}</Link>}
          {data.cta2_label && <Link to={data.cta2_href || "/shop"} className="border border-[#F7F5F1] text-[#F7F5F1] px-8 py-3.5 text-xs uppercase tracking-[0.22em] font-mono hover:bg-[#F7F5F1] hover:text-[#1D1D1B] transition-colors" data-testid="hero-cta-secondary">{data.cta2_label}</Link>}
        </div>
      </div>
    </section>
  );
}

function BrandStatement({ data }) {
  return (
    <section className="onusa-container py-20 md:py-32 text-center max-w-3xl" data-testid="section-brand-statement">
      <div className="flex items-center justify-center gap-3 mb-6"><span className="w-10 rule-x" /><Eyebrow>{data.eyebrow}</Eyebrow><span className="w-10 rule-x" /></div>
      <h2 className="heading-x text-3xl md:text-5xl">{data.heading}</h2>
      <p className="mt-6 text-[#77736D] leading-relaxed">{data.body}</p>
    </section>
  );
}

function Editorial({ data }) {
  const left = data.align !== "right";
  return (
    <section className="onusa-container py-16 md:py-24" data-testid="section-editorial">
      <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
        <div className={left ? "order-1" : "order-1 md:order-2"}>
          <div className="aspect-[4/5] overflow-hidden bg-[#E8E1D8]">
            <img src={mediaUrl(data.image)} alt={data.heading} className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700" />
          </div>
        </div>
        <div className={left ? "order-2" : "order-2 md:order-1"}>
          <Eyebrow>{data.eyebrow}</Eyebrow>
          <h2 className="heading-x text-3xl md:text-4xl">{data.heading}</h2>
          <p className="mt-5 text-[#77736D] leading-relaxed">{data.body}</p>
          {data.cta_label && <Link to={data.cta_href || "/lookbook"} className="btn-x-outline mt-8">{data.cta_label}</Link>}
        </div>
      </div>
    </section>
  );
}

function MaterialStory({ data }) {
  return (
    <section className="bg-[#E8E1D8]/50 py-16 md:py-24" data-testid="section-material">
      <div className="onusa-container grid md:grid-cols-2 gap-8 md:gap-16 items-center">
        <div className="aspect-square overflow-hidden bg-[#E8E1D8]">
          <img src={mediaUrl(data.image)} alt={data.heading} className="w-full h-full object-cover" />
        </div>
        <div>
          <Eyebrow>{data.eyebrow}</Eyebrow>
          <h2 className="heading-x text-3xl md:text-5xl">{data.heading}</h2>
          <p className="mt-5 text-[#77736D] leading-relaxed">{data.body}</p>
        </div>
      </div>
    </section>
  );
}

function Philosophy({ data }) {
  return (
    <section className="onusa-container py-16 md:py-24" data-testid="section-philosophy">
      <div className="text-center mb-12"><Eyebrow>{data.eyebrow}</Eyebrow><h2 className="heading-x text-2xl md:text-4xl">{data.heading}</h2></div>
      <div className="grid md:grid-cols-3 gap-px bg-[#E8E1D8]">
        {(data.cards || []).map((c, i) => (
          <div key={i} className="bg-[#F7F5F1] p-10 text-center">
            <p className="font-mono text-xs tracking-[0.3em] text-[#77736D]">0{i + 1}</p>
            <h3 className="heading-x text-xl mt-4">{c.title}</h3>
            <p className="mt-3 text-sm text-[#77736D]">{c.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Newsletter({ data }) {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data: d } = await api.post("/newsletter", { email, consent: true });
      toast.success(d.message);
      setEmail("");
    } catch { toast.error("Probeer het opnieuw"); }
  };
  return (
    <section className="bg-[#1D1D1B] text-[#F7F5F1] py-20" data-testid="section-newsletter">
      <div className="onusa-container text-center max-w-xl">
        <h2 className="heading-x text-2xl md:text-4xl">{data.heading}</h2>
        <p className="mt-4 text-[#F7F5F1]/70">{data.body}</p>
        <form onSubmit={submit} className="mt-8 flex items-center border-b border-[#F7F5F1]/40 max-w-md mx-auto">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="JOUW E-MAIL" className="flex-1 bg-transparent py-3 outline-none font-mono text-xs tracking-[0.18em] placeholder:text-[#F7F5F1]/40" data-testid="home-newsletter-input" />
          <button className="font-mono text-xs tracking-[0.2em]" data-testid="home-newsletter-submit">AANMELDEN →</button>
        </form>
      </div>
    </section>
  );
}

const RENDERERS = { hero: Hero, brand_statement: BrandStatement, featured_products: FeaturedProducts, editorial: Editorial, material_story: MaterialStory, brand_philosophy: Philosophy, newsletter: Newsletter };

export default function Home() {
  const [sections, setSections] = useState([]);
  useEffect(() => { api.get("/homepage").then(({ data }) => setSections(data)).catch(() => {}); }, []);
  return (
    <div data-testid="home-page">
      {sections.map((s) => {
        const R = RENDERERS[s.type];
        return R ? <R key={s.id} data={s.data || {}} /> : null;
      })}
    </div>
  );
}
